/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {useState, type CSSProperties} from "react";
import type {Cell} from "@tanstack/react-table";
import {flexRender} from "@tanstack/react-table";
import {IconButton} from "@atlaskit/button/new";
import DeleteIcon from "@atlaskit/icon/core/delete";
import EditIcon from "@atlaskit/icon/core/edit";
import EyeOpenIcon from "@atlaskit/icon/core/eye-open";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import type {TableColumnDef, TableConfig} from "~/components/table/table.types";
import {clsx, isTableEmptyCellValue, TABLE_EMPTY_CELL_DISPLAY} from "~/components/table/tableUtils.tsx";
import {Badge, TagList} from "~/components/table/Badge";
import {TableActionsColumn} from "~/components/table/TableActionsColumn";
import {Skeleton} from "~/components/table/Skeleton";
import {AnankeTooltip} from "@vesta/ananke/components/anankeTooltip";

const EyeIcon = EyeOpenIcon;
const MoreIcon = ShowMoreHorizontalIcon;

interface TableDataCellProps<TRow> {
    cell: Cell<TRow, unknown>
    row: TRow
    config: TableConfig<TRow>
    isSkeletonRow: boolean
}

export function TableDataCell<TRow>({cell, row, config, isSkeletonRow}: TableDataCellProps<TRow>) {
    const meta = cell.column.columnDef.meta as Record<string, unknown> | undefined;
    const colType = meta?.type as string | undefined;
    const align = (meta?.align as string) ?? "left";
    const isPinned = cell.column.getIsPinned() as "left" | "right" | false;
    const colDef = meta?.colDef as TableColumnDef<TRow> | undefined;

    const stopRowClickPropagation = colType === "actions" || colType === "checkbox";

    return (
        <td
            className={clsx(
                "table-td",
                colType === "checkbox" && "table-td--checkbox",
                colType === "actions" && "table-td--actions",
                colType === "actions" && align === "left" && "table-td--actions-left",
                colType === "actions" && align === "right" && "table-td--actions-right",
                isPinned === "left" && "table-td--pinned-left",
                isPinned === "right" && "table-td--pinned-right",
            )}
            onClick={stopRowClickPropagation ? (e) => e.stopPropagation() : undefined}
            style={{
                width: cell.column.getSize(),
                textAlign: align as CSSProperties["textAlign"],
                ...(isPinned === "left"
                    ? {left: `${cell.column.getStart("left")}px`}
                    : isPinned === "right"
                      ? {right: `${cell.column.getAfter("right")}px`}
                    : {}),
            }}
        >
            <div className="table-td-inner" style={colType === "checkbox" ? {justifyContent: "center"} : undefined}>
                {isSkeletonRow ? (
                    <Skeleton />
                ) : colType === "checkbox" ? (
                    flexRender(cell.column.columnDef.cell, cell.getContext())
                ) : colType === "actions" ? (
                    config.rowActionsConfig !== undefined ? (
                        <TableActionsColumn row={row} config={config.rowActionsConfig} />
                    ) : (
                        <ActionsCell row={row} config={config} />
                    )
                ) : colDef?.cellRenderer ? (
                    colDef.cellRenderer(row)
                ) : (
                    <DefaultCell value={cell.getValue()} colDef={colDef} />
                )}
            </div>
        </td>
    );
}

function ActionsCell<TRow>({row, config}: {row: TRow; config: TableConfig<TRow>}) {
    const [moreOpen, setMoreOpen] = useState(false);

    const visibleActions =
        config.rowActions?.filter((a) => {
            return a.isVisible ? a.isVisible(row) : true;
        }) ?? [];

    return (
        <div className="table-actions-cell table-actions-cell--view-edit">
            {config.onViewRow ? (
                <AnankeTooltip
                    content="View"
                    theme="light"
                >
                    <IconButton
                        icon={EyeIcon}
                        label="View"
                        appearance="subtle"
                        spacing="compact"
                        onClick={(event) => {
                            event.stopPropagation();
                            config.onViewRow?.(row);
                        }}
                    />
                </AnankeTooltip>
            ) : null}
            {config.onEditRow ? (
                <AnankeTooltip
                    content="Edit"
                    theme="light"
                >
                    <IconButton
                        icon={EditIcon}
                        label="Edit"
                        appearance="subtle"
                        spacing="compact"
                        isDisabled={!(config.canEditRow?.(row) ?? true)}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (config.canEditRow?.(row) === false) {
                                return;
                            }
                            config.onEditRow?.(row);
                        }}
                    />
                </AnankeTooltip>
            ) : null}
            {config.onDeleteRow ? (
                <AnankeTooltip
                    content="Delete"
                    theme="light"
                >
                    <IconButton
                        icon={DeleteIcon}
                        label="Delete"
                        appearance="subtle"
                        spacing="compact"
                        isDisabled={!(config.canDeleteRow?.(row) ?? true)}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (config.canDeleteRow?.(row) === false) {
                                return;
                            }
                            config.onDeleteRow?.(row);
                        }}
                    />
                </AnankeTooltip>
            ) : null}
            {config.renderInlineActions ? config.renderInlineActions(row) : null}
            {visibleActions.length > 0 ? (
                <div className="table-more-wrapper">
                    <AnankeTooltip
                        content="More"
                        theme="light"
                    >
                        <IconButton
                            icon={MoreIcon}
                            label="More"
                            appearance="subtle"
                            spacing="compact"
                            onClick={(event) => {
                                event.stopPropagation();
                                setMoreOpen((v) => !v);
                            }}
                        />
                    </AnankeTooltip>
                    {moreOpen ? (
                        <>
                            <div className="table-more-dismiss" aria-hidden onClick={() => setMoreOpen(false)} />
                            <div className="table-more-dropdown">
                            {visibleActions.map((action) => (
                                <button
                                    key={action.id}
                                    type="button"
                                    className="table-more-item"
                                    disabled={action.isDisabled?.(row)}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        action.onClick(row);
                                        setMoreOpen(false);
                                    }}
                                >
                                    {action.icon ? <span className="table-more-item-icon">{action.icon}</span> : null}
                                    <span>{action.label}</span>
                                </button>
                            ))}
                            </div>
                        </>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function TableEmptyCell() {
    return <span className="table-cell-empty">{TABLE_EMPTY_CELL_DISPLAY}</span>;
}

function DefaultCell<TRow>({value, colDef}: {value: unknown; colDef?: TableColumnDef<TRow>}) {
    if (isTableEmptyCellValue(value)) {
        return <TableEmptyCell />;
    }

    if (colDef?.type === "badge") {
        return <Badge value={String(value)} config={colDef.badgeMap?.[String(value)]} />;
    }

    if (colDef?.type === "tag-list") {
        const arr = (Array.isArray(value) ? value.map(String) : [String(value)]).filter(
            (item) => !isTableEmptyCellValue(item),
        );
        if (arr.length === 0) {
            return <TableEmptyCell />;
        }
        return <TagList values={arr} badgeMap={colDef.badgeMap} />;
    }

    if (colDef?.type === "link") {
        const href = String(value);
        const display = colDef.valueFormatter ? String(colDef.valueFormatter(value)) : href;
        if (isTableEmptyCellValue(display) || isTableEmptyCellValue(href)) {
            return <TableEmptyCell />;
        }
        return (
            <a href={href} className="table-link" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                {display}
            </a>
        );
    }

    if (colDef?.valueFormatter && typeof value !== "object") {
        const formatted = colDef.valueFormatter(value);
        if (isTableEmptyCellValue(formatted)) {
            return <TableEmptyCell />;
        }
        return (
            <span className={clsx("table-cell-text", colDef.truncate && "table-cell-text--truncate")}>
                {String(formatted)}
            </span>
        );
    }

    return (
        <span className={clsx("table-cell-text", colDef?.truncate && "table-cell-text--truncate")}>{String(value)}</span>
    );
}
